
import { useMemo } from 'react';
import Link from '@/components/compat/Link';
import { ArrowLeft, Star, Clock, FileText, Calendar } from 'lucide-react';
import { useCms } from '@/components/painel/cms/cms-context';
import { CmsCategoryIcon } from '@/components/painel/cms/cms-icons';
import {
  GuidePremiumBadge,
  GuidePremiumCardShell,
} from '@/components/help-center/GuidePremiumCard';
import { buildPublicGuideList } from '@/lib/cms-guides';
import { mergeCategoriesWithHomeDefaults } from '@/lib/help-center-home-categories';
import { HOME_PAGE_CATEGORY_COLOR_CLASSES } from '@/lib/help-center-home-categories';
import { categoryNames } from '@/lib/articles';
import { getGuideHref } from '@/lib/guide-routes';
import { cn } from '@/lib/utils';

const categoryMeta: Record<
  string,
  { description: string; color: string; iconBg: string; iconText: string }
> = {
  'primeiros-passos': {
    description:
      'Novo por aqui? Aprenda a usar o chat, enviar mensagens, personalizar seu perfil e muito mais.',
    color: 'indigo',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
  },
  account: {
    description: 'Configure seu perfil, gerencie credenciais e personalize sua experiência no xat.',
    color: 'blue',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
  },
  safety: {
    description: 'Proteja sua conta, entenda nossas diretrizes e aprenda a denunciar conteúdo impróprio.',
    color: 'emerald',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
  },
  powers: {
    description: 'Desbloqueie funcionalidades exclusivas e personalize seu chat com powers especiais.',
    color: 'amber',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
  },
  chats: {
    description: 'Crie, personalize e modere suas próprias salas de chat com ferramentas avançadas.',
    color: 'sky',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
  },
  store: {
    description: 'Métodos de pagamento seguros, gerenciamento de compras e políticas de reembolso.',
    color: 'violet',
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-600',
  },
  troubleshooting: {
    description: 'Resolva problemas técnicos, erros comuns e otimize sua experiência no xat.',
    color: 'rose',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
  },
  mobile: {
    description: 'Guias completos para usar o xat em dispositivos móveis iOS e Android.',
    color: 'cyan',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-600',
  },
  'xat-live': {
    description: 'Chamadas de voz e vídeo, streaming ao vivo e recursos de comunicação em tempo real.',
    color: 'fuchsia',
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600',
  },
};

interface CategoryViewProps {
  category: string;
  onSelectArticle?: (articleId: number) => void;
  onBack?: () => void;
}

export default function CategoryView({ category, onSelectArticle, onBack }: CategoryViewProps) {
  const { categories, articles: cmsArticles, hydrated } = useCms();
  const cmsRow = useMemo(
    () => mergeCategoriesWithHomeDefaults(categories).find((c) => c.id === category),
    [categories, category]
  );
  const articles = useMemo(() => {
    if (!hydrated) return [];
    return buildPublicGuideList(category, cmsArticles);
  }, [hydrated, category, cmsArticles]);
  const name = cmsRow?.name ?? categoryNames[category] ?? category;
  const meta = categoryMeta[category] ?? categoryMeta.account;
  const colorKey =
    cmsRow?.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[cmsRow.color]
      ? cmsRow.color
      : meta.color;
  const categoryIcon = cmsRow?.icon ?? 'FileText';
  const description = cmsRow?.description?.trim() ? cmsRow.description : meta.description;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="sticky top-20 z-40 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-center gap-3">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Categorias
              </button>
            ) : (
              <Link
                href="/"
                className="flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Categorias
              </Link>
            )}
            <span className="text-gray-300">/</span>
            <span className="font-poppins text-sm font-semibold text-gray-900">{name}</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-10">
          <div className="mb-3 flex items-center gap-3">
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', meta.iconBg)}>
              <CmsCategoryIcon name={categoryIcon} className={cn('h-5 w-5', meta.iconText)} />
            </div>
            <h1 className="font-poppins text-3xl font-bold text-gray-900">{name}</h1>
          </div>
          <p className="max-w-2xl font-poppins text-base leading-relaxed text-gray-500">{description}</p>
          <p className="mt-3 text-sm font-medium text-gray-400">
            {articles.length} {articles.length === 1 ? 'artigo' : 'artigos'}
          </p>
        </div>

        {articles.length > 0 ? (
          <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 lg:grid-cols-3" role="list">
            {articles.map((article) => {
              const href = getGuideHref(category, article.slug);
              const card = (
                <GuidePremiumCardShell
                  colorKey={colorKey}
                  icon={article.icon || categoryIcon}
                  title={article.title}
                  badge={
                    article.featured || article.popular || article.recentlyUpdated ? (
                      <>
                        {article.featured || article.popular ? (
                          <GuidePremiumBadge
                            colorKey={colorKey}
                            className="inline-flex items-center gap-1"
                          >
                            <Star className="h-2.5 w-2.5 fill-current" aria-hidden />
                            Popular
                          </GuidePremiumBadge>
                        ) : null}
                        {article.recentlyUpdated ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 font-poppins text-[10px] font-semibold leading-none text-emerald-700">
                            <Calendar className="h-2.5 w-2.5" aria-hidden />
                            Recente
                          </span>
                        ) : null}
                      </>
                    ) : undefined
                  }
                  footer={
                    <span className="inline-flex items-center gap-1 tabular-nums">
                      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {article.updatedAt}
                    </span>
                  }
                />
              );

              if (onSelectArticle) {
                return (
                  <li key={article.key} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => onSelectArticle(article.slug as unknown as number)}
                      className="block w-full text-left"
                    >
                      {card}
                    </button>
                  </li>
                );
              }

              return (
                <li key={article.key} className="min-w-0">
                  <Link href={href} className="block h-full">
                    {card}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : !hydrated ? (
          <div className="py-20 text-center text-gray-400">
            <FileText className="mx-auto mb-4 h-12 w-12 opacity-40" />
            <p className="font-medium">Carregando artigos…</p>
          </div>
        ) : (
          <div className="py-20 text-center text-gray-400">
            <FileText className="mx-auto mb-4 h-12 w-12 opacity-40" />
            <p className="font-medium">Nenhum artigo encontrado nesta categoria.</p>
          </div>
        )}
      </div>
    </div>
  );
}
